import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";
import { MandatoryField } from "../../../../common/MandatoryFieldsIcon/MandatoryField";
import "./WebLinkDialog.css";

const URL_PROTOCOL_REGEX = /^https?:\/\//i;

/**
 * Single-field Yup schema for the external URL. Keeping it module-scoped
 * means it is built once and reused across renders.
 *  - required → "Please enter a valid link."
 *  - matches  → "URL must start with http:// or https://"
 */
const urlSchema = Yup.string()
  .trim()
  .required("Please enter a valid link.")
  .matches(URL_PROTOCOL_REGEX, "URL must start with http:// or https://");

/** Run the schema synchronously and return the first error message (or ""). */
const validateUrl = (raw) => {
  try {
    urlSchema.validateSync(raw, { abortEarly: true });
    return "";
  } catch (err) {
    return err?.message || "Please enter a valid link.";
  }
};

/**
 * WebLinkDialog
 * Popup for entering the external web URL used by the "External Web Content"
 * content-redirect option. Owns input state, validation, the "Test Link"
 * helper, and the +Add / Cancel actions.
 */
const WebLinkDialog = ({ open, initialUrl, onClose, onSubmit }) => {
  const [url, setUrl] = useState(initialUrl || "");
  const [error, setError] = useState("");

  // Reset internal state whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setUrl(initialUrl || "");
      setError("");
    }
  }, [open, initialUrl]);

  const trimmed = url.trim();
  const isTestEnabled = trimmed.length > 0 && URL_PROTOCOL_REGEX.test(trimmed);

  const handleChange = (event) => {
    const next = event.target.value;
    setUrl(next);
    // Live validation: only flag protocol problems while the user is typing.
    // The "required" error is reserved for clicking +Add on an empty field,
    // so the hint stays visible until they actually try to submit.
    if (next.trim().length === 0) {
      setError("");
    } else {
      setError(validateUrl(next));
    }
  };

  const handleTestLink = () => {
    if (!isTestEnabled) return;
    window.open(trimmed, "_blank", "noopener,noreferrer");
  };

  const handleAdd = () => {
    const message = validateUrl(url);
    if (message) {
      setError(message);
      return;
    }
    onSubmit(trimmed);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="web-link-dialog"
      aria-labelledby="web-link-dialog-title"
      maxWidth="md"
    >
      <DialogTitle
        id="web-link-dialog-title"
        className="web-link-dialog__title"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span>Web Link</span>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="web-link-dialog__content">
        <label htmlFor="web-link-input" className="web-link-dialog__label">
          External URL<MandatoryField />
        </label>
        <div className="web-link-dialog__row">
          <div className="web-link-dialog__input-wrap">
            <LanguageIcon
              className="web-link-dialog__input-icon"
              fontSize="small"
            />
            <input
              id="web-link-input"
              type="url"
              className="form-label form-control web-link-dialog__input"
              placeholder="Enter external weblink"
              value={url}
              onChange={handleChange}
              autoFocus
            />
          </div>
          <Button
            variant="contained"
            onClick={handleTestLink}
            disabled={!isTestEnabled}
          >
            Test Link
          </Button>
        </div>
        <span
          className={`web-link-dialog__hint ${
            error ? "web-link-dialog__hint--error" : ""
          }`}
        >
          {error || (
            <>
              URL must start with <strong>http://</strong> or{" "}
              <strong>https://</strong> (e.g., https://example.com)
            </>
          )}
        </span>
      </DialogContent>

      <DialogActions className="web-link-dialog__actions">
        <Button
          variant="outlined"
          className="web-link-dialog__btn web-link-dialog__btn--test"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="web-link-dialog__btn web-link-dialog__btn--primary"
          onClick={handleAdd}
        >
          + Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WebLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

WebLinkDialog.defaultProps = {
  initialUrl: "",
};

export default WebLinkDialog;
